/**
 * @fileoverview Application Shell Component
 * Provides consistent header, main content area, and footer layout.
 * @module shared/components/AppShell
 *
 * CONTRACT:
 * - renderAppShell({ route, content }) renders the full page layout
 * - Header contains brand, tagline, and navigation
 * - Main contains page content
 * - Footer contains metadata
 * - All CSS classes follow cap-shell-* naming convention
 */

import { el } from "./simpleRender.js";

/**
 * Navigation configuration defining all main routes.
 * Simplified navigation matching mockup design.
 * @type {Array<{label: string, href: string, id: string, disabled?: boolean}>}
 */
const NAV_ITEMS = [
  { label: "Home", href: "#/home", id: "home" },
  { label: "Observatory", href: "#/observatories", id: "observatories", disabled: true },
  { label: "Signals", href: "#/signals", id: "signals" },
  { label: "Dispatches", href: "#/dispatches", id: "dispatches" },
  { label: "Forum", href: "#/forum", id: "forum", disabled: true },
  { label: "Manifesto", href: "#/manifesto", id: "manifesto" },
  { label: "About", href: "#/about", id: "about" },
];

/**
 * Site branding configuration
 */
const SITE_CONFIG = {
  name: "CAPITULATORS.IO",
  tagline: "Where markets go to give up.",
};

/**
 * Determines if a nav item is active based on current route.
 * @param {string} navId - Navigation item ID
 * @param {import('../../core/router.js').Route} route - Current route
 * @returns {boolean} True if nav item is active
 */
function isNavItemActive(navId, route) {
  const page = route.page || "home";
  
  // Home is active only for exact home route
  if (navId === "home") {
    return page === "home";
  }
  
  // Observatories is active for both list and individual observatory pages
  if (navId === "observatories") {
    return page === "observatories" || page === "observatory";
  }
  
  // All other pages match directly
  return page === navId;
}

/**
 * Renders the skip-to-content accessibility link.
 * @returns {HTMLElement} Skip link element
 */
function renderSkipLink() {
  return el("a", { 
    href: "#main-content", 
    className: "cap-skip-link",
    "aria-label": "Skip to main content"
  }, ["Skip to content"]);
}

/**
 * Renders the header with brand, tagline, and navigation.
 * @param {import('../../core/router.js').Route} route - Current route for active state
 * @returns {HTMLElement} Header element
 */
function renderHeader(route) {
  const navItems = NAV_ITEMS.map(item => {
    const isActive = isNavItemActive(item.id, route);
    const isDisabled = item.disabled === true;
    
    let className = "cap-nav-link";
    if (isActive) className += " cap-nav-link--active";
    if (isDisabled) className += " cap-nav-link--disabled";
    
    // Disabled items render as span, not anchor
    if (isDisabled) {
      return el("span", { 
        className,
        "aria-label": `${item.label} (coming soon)`,
        title: "Coming soon"
      }, [item.label]);
    }
    
    return el("a", { 
      href: item.href, 
      className,
      "aria-current": isActive ? "page" : undefined,
      "aria-label": item.label
    }, [item.label]);
  });

  return el("header", { className: "cap-shell-header" }, [
    el("div", { className: "cap-shell-header-inner" }, [
      // Brand section with logo dot, name, and tagline
      el("div", { className: "cap-shell-brand-section" }, [
        el("a", { href: "#/", className: "cap-shell-brand", "aria-label": "Capitulators.io Home" }, [
          el("span", { className: "cap-brand-dot" }, []),
          el("span", { className: "cap-brand-name" }, [SITE_CONFIG.name]),
        ]),
        el("span", { className: "cap-brand-tagline" }, [SITE_CONFIG.tagline]),
      ]),
      // Navigation
      el("nav", { className: "cap-shell-nav", "aria-label": "Main navigation" }, navItems),
      // Mobile menu toggle
      el("button", { 
        className: "cap-shell-menu-toggle", 
        "aria-label": "Toggle navigation menu",
        "aria-expanded": "false",
        type: "button"
      }, [
        el("span", { className: "cap-menu-bar" }, []),
        el("span", { className: "cap-menu-bar" }, []),
        el("span", { className: "cap-menu-bar" }, []),
      ]),
    ]),
  ]);
}

/**
 * Footer configuration
 */
const FOOTER_CONFIG = {
  version: "0.9.2-alpha",
  buildDate: "2024.12",
  dataSources: [
    { name: "FRED", desc: "Federal Reserve Economic Data" },
    { name: "BLS", desc: "Bureau of Labor Statistics" },
    { name: "Treasury", desc: "U.S. Department of the Treasury" },
  ],
  links: [
    { label: "Manifesto", href: "#/manifesto" },
    { label: "Signals", href: "#/signals" },
    { label: "About", href: "#/about" },
  ],
};

/**
 * Renders the alpha banner with X DM access link
 * @returns {HTMLElement} Banner element
 */
function renderAlphaBanner() {
  // Check if banner was dismissed recently
  const dismissedAt = localStorage.getItem('alpha-banner-dismissed');
  if (dismissedAt) {
    const daysSinceDismiss = (Date.now() - parseInt(dismissedAt)) / (1000 * 60 * 60 * 24);
    if (daysSinceDismiss < 7) {
      return el('div', {}, []); // Return empty if dismissed within 7 days
    }
  }
  
  const handleDismiss = () => {
    localStorage.setItem('alpha-banner-dismissed', Date.now().toString());
    const banner = document.querySelector('.alpha-banner');
    if (banner) {
      banner.classList.add('alpha-banner--dismissed');
      setTimeout(() => banner.remove(), 300);
    }
  };
  
  return el('div', { className: 'alpha-banner' }, [
    el('div', { className: 'alpha-banner__inner' }, [
      el('div', { className: 'alpha-banner__content' }, [
        el('span', { className: 'alpha-banner__badge' }, ['ALPHA']),
        el('span', { className: 'alpha-banner__text' }, [
          'Live signals · Expanding coverage · Selective access'
        ]),
      ]),
      el('a', { 
        className: 'alpha-banner__x-link',
        href: 'https://x.com/CapitulationStn',
        target: '_blank',
        rel: 'noopener noreferrer',
        'aria-label': 'Request access via X DM'
      }, [
        el('span', { className: 'alpha-banner__x-icon' }, ['𝕏']),
        el('span', { className: 'alpha-banner__x-text' }, ['DM @CapitulationStn for access']),
        el('span', { className: 'alpha-banner__x-arrow' }, ['→'])
      ]),
      el('button', { 
        className: 'alpha-banner__dismiss',
        onclick: handleDismiss,
        'aria-label': 'Dismiss banner',
        type: 'button',
      }, ['×']),
    ]),
  ]);
}

/**
 * Renders the footer.
 * @returns {HTMLElement} Footer element
 */
function renderFooter() {
  const currentYear = new Date().getFullYear();
  
  // Footer links
  const footerLinks = FOOTER_CONFIG.links.map(link =>
    el("a", { href: link.href, className: "cap-footer-link" }, [link.label])
  );
  
  // Data sources list
  const dataSources = FOOTER_CONFIG.dataSources.map(source =>
    el("span", { className: "cap-footer-source", title: source.desc }, [source.name])
  );
  
  return el("footer", { className: "cap-shell-footer" }, [
    // Top divider line
    el("div", { className: "cap-footer-divider" }, []),
    
    el("div", { className: "cap-shell-footer-inner" }, [
      // Left column: Brand + System Status
      el("div", { className: "cap-footer-brand" }, [
        el("div", { className: "cap-footer-logo" }, [
          el("span", { className: "cap-footer-logo-dot" }, []),
          el("span", { className: "cap-footer-logo-text" }, ["CAPITULATORS.IO"]),
        ]),
        el("div", { className: "cap-footer-status" }, [
          el("span", { className: "cap-footer-status-dot" }, []),
          el("span", { className: "cap-footer-status-text" }, ["Systems Operational"]),
        ]),
      ]),
      
      // Center column: Data Provenance
      el("div", { className: "cap-footer-provenance" }, [
        el("div", { className: "cap-footer-provenance-label" }, ["Data Infrastructure"]),
        el("div", { className: "cap-footer-sources" }, [
          el("span", { className: "cap-footer-glyph" }, ["◆"]),
          ...dataSources.flatMap((source, i) => 
            i < dataSources.length - 1 
              ? [source, el("span", { className: "cap-footer-separator" }, ["•"])] 
              : [source]
          ),
          el("span", { className: "cap-footer-glyph" }, ["◆"]),
        ]),
        el("div", { className: "cap-footer-series-count" }, ["120+ macro signals tracked"]),
      ]),
      
      // Right column: Links
      el("div", { className: "cap-footer-nav" }, [
        el("nav", { className: "cap-footer-links", "aria-label": "Footer navigation" }, footerLinks),
      ]),
    ]),
    
    // Bottom row: Copyright + Build
    el("div", { className: "cap-footer-bottom" }, [
      el("div", { className: "cap-footer-copyright" }, [
        `© ${currentYear} Capitulators.io`
      ]),
      el("div", { className: "cap-footer-build" }, [
        el("span", { className: "cap-footer-version" }, [`v${FOOTER_CONFIG.version}`]),
        el("span", { className: "cap-footer-build-sep" }, ["•"]),
        el("span", { className: "cap-footer-build-date" }, [`Build ${FOOTER_CONFIG.buildDate}`]),
      ]),
      el("div", { className: "cap-footer-disclaimer" }, [
        "Past capitulation is no guarantee of future capitulation."
      ]),
    ]),
  ]);
}

/**
 * Renders the complete application shell.
 * @param {Object} options - Render options
 * @param {import('../../core/router.js').Route} options.route - Current route
 * @param {HTMLElement|string} options.content - Page content to render
 * @param {boolean} [options.hideShell=false] - If true, renders content without header/footer (for Stationgate)
 * @returns {HTMLElement} Complete shell element
 */
export function renderAppShell({ route, content, hideShell = false }) {
  // For Stationgate (splash page), render without chrome
  if (hideShell) {
    return el("div", { className: "cap-shell cap-shell--fullscreen" }, [
      renderSkipLink(),
      el("main", { 
        id: "main-content", 
        className: "cap-shell-main cap-shell-main--fullscreen",
        tabindex: "-1"
      }, [
        typeof content === "string" ? content : content
      ]),
    ]);
  }

  return el("div", { className: "cap-shell" }, [
    renderSkipLink(),
    renderHeader(route),
    renderAlphaBanner(),
    el("main", { 
      id: "main-content", 
      className: "cap-shell-main",
      tabindex: "-1"
    }, [
      typeof content === "string" ? content : content
    ]),
    renderFooter(),
  ]);
}

/**
 * Sets up mobile menu toggle functionality.
 * Call this after rendering to attach event listeners.
 */
export function setupMobileMenu() {
  const toggle = document.querySelector(".cap-shell-menu-toggle");
  const nav = document.querySelector(".cap-shell-nav");
  
  if (!toggle || !nav) return;
  
  toggle.addEventListener("click", () => {
    const isExpanded = toggle.getAttribute("aria-expanded") === "true";
    toggle.setAttribute("aria-expanded", String(!isExpanded));
    nav.classList.toggle("cap-shell-nav--open");
    toggle.classList.toggle("cap-shell-menu-toggle--open");
  });
  
  // Close menu when clicking a nav link
  nav.querySelectorAll(".cap-nav-link").forEach(link => {
    link.addEventListener("click", () => {
      toggle.setAttribute("aria-expanded", "false");
      nav.classList.remove("cap-shell-nav--open");
      toggle.classList.remove("cap-shell-menu-toggle--open");
    });
  });
}

export { NAV_ITEMS, SITE_CONFIG };
